import React from "react";
import { Typography, Box, Grid, Stack, Button, Switch, styled, Chip } from "@mui/material";
import { ReactComponent as SignaturewafIcon } from "../../../../vendor/_new/icon/sites/signature_waf.svg";
import { ReactComponent as GlobalIcon } from "../../../../vendor/_new/icon/services/global.svg";
import { ReactComponent as ArrowrightIcon } from "../../../../vendor/_new/icon/sites/arrow_right.svg";
import { ReactComponent as ArrowleftIcon } from "../../../../vendor/_new/icon/sites/arrow_left.svg";

import colors from "../../../../utils/colors";
import useWAFConfig from "../../../../hooks/user/useWAFConfig";
import usePermissions from "../../../../hooks/usePermissions";
import { containedButtonSx, outlinedButtonSx } from "../../../common/Button";

const BorderedGreenSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: colors.secondary[300],
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: colors.secondary[100],
    border: `1px solid ${colors.green[200]}`,
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
  },
}));

const StatusBadge = ({ enabled }) => (
  <Chip
    label={enabled ? "Enabled" : "Disabled"}
    size="small"
    sx={{
      fontSize: 12,
      lineHeight: "18px",
      backgroundColor: enabled ? colors.green[10] : colors.neutral[100],
      border: `1px solid ${enabled ? colors.green[200] : colors.neutral[300]}`,
      color: enabled ? colors.green[300] : colors.neutral[600],
      height: 24,
    }}
  />
);

/** Sites where Add-site WAF Continue already persisted toggles (survives remount). */
const wafTogglesSavedSiteUids = new Set();

const WAFConfiguration = ({ handleNext, handlePrev, siteUid }) => {
  const { wafConfig, configWafSetting } = useWAFConfig();
  const { hasPermission, PERMISSIONS } = usePermissions();
  const [loading, setLoading] = React.useState(false);
  // WAF Configuration
  const [sd_sig_module_active, set_sd_sig_module_active] = React.useState(false);
  const [smart_parse_module_active, set_smart_parse_module_active] = React.useState(true);

  // Check if user can configure WAF
  const canConfigureWAF = hasPermission(PERMISSIONS.WAF_CONFIGURE);
  // Keep switches visually enabled during Continue save; handlers ignore clicks when loading.
  const switchDisabled = !canConfigureWAF;

  React.useEffect(() => {
    if (wafConfig) {
      set_sd_sig_module_active(wafConfig?.sd_sig_module_active ?? false);
      set_smart_parse_module_active(wafConfig?.smart_parse_module_active ?? true);
    }
  }, [wafConfig]);

  // Until Continue saves for this siteUid: if API returns Smart Parse off, default local ON.
  // After a successful save, never re-force ON on Back remount (LE-389).
  React.useEffect(() => {
    if (!wafConfig || !siteUid) return;
    if (wafTogglesSavedSiteUids.has(siteUid)) return;
    if (wafConfig.smart_parse_module_active === false) {
      set_smart_parse_module_active(true);
    }
  }, [wafConfig, siteUid]);

  const sdSigWafActiveChange = () => {
    if (!canConfigureWAF || loading) return;
    set_sd_sig_module_active((prev) => !prev);
  };
  const smartParseWafActiveChange = () => {
    if (!canConfigureWAF || loading) return;
    set_smart_parse_module_active((prev) => !prev);
  };

  const handleContinue = async () => {
    if (!siteUid || loading) return;
    setLoading(true);
    try {
      await configWafSetting(siteUid, "bulkWafToggleChange", {
        smart_parse_module_active,
        sd_sig_module_active,
      });
      wafTogglesSavedSiteUids.add(siteUid);
      handleNext();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800], pb: 2 }}>
          Configure WAF protection for your application
        </Typography>
        <Box sx={{ py: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: "8px",
                  border: `solid 1px ${colors.neutral[200]}`,
                  backgroundColor: "#fff",
                  px: 4,
                  py: 4,
                  height: 120,
                }}
              >
                <Stack spacing={2} sx={{ height: "100%" }}>
                  <Stack direction={"row"} alignItems={"flex-start"} justifyContent={"space-between"}>
                    <Stack direction={"row"} alignItems={"center"} spacing={2}>
                      <GlobalIcon style={{ width: 40, height: 40, color: colors.primary[300] }} />
                      <Stack spacing={0.5}>
                        <Typography
                          sx={ { fontSize: 16, lineHeight: "24px", color: colors.neutral[1000], fontWeight: "medium" }}
                        >
                          Layerd AI Smart Parse
                        </Typography>
                        <Typography
                          sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[600] }}
                        >
                          AI-powered request parsing
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                    <StatusBadge enabled={smart_parse_module_active} />
                    <BorderedGreenSwitch
                      checked={smart_parse_module_active}
                      onChange={smartParseWafActiveChange}
                      disabled={switchDisabled}
                    />
                  </Stack>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: "8px",
                  border: `solid 1px ${colors.neutral[200]}`,
                  backgroundColor: "#fff",
                  px: 4,
                  py: 4,
                  height: 120,
                }}
              >
                <Stack spacing={2} sx={{ height: "100%" }}>
                  <Stack direction={"row"} alignItems={"flex-start"} justifyContent={"space-between"}>
                    <Stack direction={"row"} alignItems={"center"} spacing={2}>
                      <SignaturewafIcon style={{ width: 40, height: 40, color: colors.primary[300] }} />
                      <Stack spacing={0.5}>
                        <Typography
                          sx={ { fontSize: 16, lineHeight: "24px", color: colors.neutral[1000], fontWeight: "medium" }}
                        >
                          Signature WAF
                        </Typography>
                        <Typography
                          sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[600] }}
                        >
                          Layerd Signature Detection
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                    <StatusBadge enabled={sd_sig_module_active} />
                    <BorderedGreenSwitch
                      checked={sd_sig_module_active}
                      onChange={sdSigWafActiveChange}
                      disabled={switchDisabled}
                    />
                  </Stack>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Stack direction={"row"} alignItems={"center"} spacing={4} sx={{ py: 2 }}>
          <Button
            startIcon={<ArrowleftIcon style={{ color: "#6371F7" }} />}
            onClick={handlePrev}
            sx={{ height: 36, ...outlinedButtonSx }}
          >
            Back
          </Button>
          <Button
            endIcon={<ArrowrightIcon style={{ color: "#fff" }} />}
            onClick={handleContinue}
            disabled={loading}
            sx={{
              height: 36,
              ...containedButtonSx,
              "&.Mui-disabled": {
                backgroundColor: "#6371F7",
                borderColor: "#6371F7",
                color: "#fff",
                opacity: 0.7,
              },
            }}
          >
            Continue
          </Button>
        </Stack>
      </Box>
    </>
  );
};
export default WAFConfiguration;
